import { Link, useMatch } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { TranslationKey } from "@/locales/i18n";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";

type FutureNavigationItem = {
  emoji: string;
  labelKey: TranslationKey;
};

type NavigationItem = FutureNavigationItem & {
  to: string;
};

const futureFilterItems: FutureNavigationItem[] = [
  { emoji: "🙋", labelKey: "notifications.menu.assignedToMe" },
  { emoji: "📣", labelKey: "notifications.menu.mentionedMe" },
  { emoji: "👥", labelKey: "notifications.menu.participating" },
  { emoji: "📢", labelKey: "notifications.menu.system" },
];

function MenuEmoji({ children }: { children: string }) {
  return (
    <span aria-hidden="true" className="w-5 shrink-0 text-center text-base leading-none">
      {children}
    </span>
  );
}

function FutureMenuButton({ emoji, labelKey }: FutureNavigationItem) {
  const { t } = useI18n();

  return (
    <Button
      aria-disabled="true"
      aria-label={`${t(labelKey)}，${t("notifications.menu.comingSoon")}`}
      className="pointer-events-none h-8 w-full justify-start gap-1.5 px-2 text-xs"
      size="sm"
      tabIndex={-1}
      title={t("notifications.menu.comingSoon")}
      variant="ghost"
    >
      <MenuEmoji>{emoji}</MenuEmoji>
      <span className="truncate">{t(labelKey)}</span>
    </Button>
  );
}

function NotificationMenuLink({ emoji, labelKey, to }: NavigationItem) {
  const { t } = useI18n();
  const selected = useMatch({ path: to, end: true }) !== null;

  return (
    <Button
      aria-current={selected ? "page" : undefined}
      asChild
      className={cn("h-8 w-full justify-start gap-1.5 px-2 text-xs hover:text-primary/80", selected && "bg-muted/60 text-primary hover:text-primary")}
      size="sm"
      variant="ghost"
    >
      <Link to={to}>
        <MenuEmoji>{emoji}</MenuEmoji>
        <span className="truncate">{t(labelKey)}</span>
      </Link>
    </Button>
  );
}

export function NotificationPageNavigation() {
  const { t } = useI18n();

  return (
    <aside className="min-w-0 sm:pr-4">
      <nav aria-label={t("notifications.menu.label")} className="flex h-full flex-col gap-4">
        <div className="flex flex-col gap-1">
          <NotificationMenuLink emoji="📥" labelKey="notifications.all" to="/notifications/all" />
          {futureFilterItems.map((item) => (
            <FutureMenuButton key={item.labelKey} {...item} />
          ))}
        </div>

        <div className="flex flex-col">
          <NotificationMenuLink emoji="⚙️" labelKey="notifications.menu.settings" to="/notifications/preferences" />
        </div>
      </nav>
    </aside>
  );
}
